
import React, { useState, useEffect, useRef } from "react";
import "../styles/styles.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarAlt,
  faSearch,
  faSyncAlt,
  faTrash,
  faSortAmountDown,
  faFilter,
  faRetweet,
  faThumbsUp,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";





const SearchArea = ({
  columns,
  setColumns,
  columnIndex,
  onSearch,
  onExpand,
  isExpanded,
  apihandleSearch,
  segregatedColumns,          // ✅ add this
  setSegregatedColumns        // ✅ add this
}) => {

  const [searchInput, setSearchInput] = useState("");
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [isLoading] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [activeDropdown, setActiveDropdown] = useState(null); // Track which column's dropdown is active
  const [expandedSubcolumns, setExpandedSubcolumns] = useState([]);
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [activeFilter, setActiveFilter] = useState(""); // New state to track active filter
  const [sortOrder, setSortOrder] = useState(null);
  

  const handleIconClick = (index) => {
    setActiveDropdown(activeDropdown === index ? null : index); // Toggle visibility based on column index
  };

  // Handle date changes
  const handleFromDateChange = (e) => {
    setFromDate(e.target.value);
  };

  const handleToDateChange = (e) => {
    setToDate(e.target.value);
  };

  // Function to reset all inputs and selections
  const handleReset = () => {
    setSearchInput(""); // Clear search input
    setSelectedPlatforms([]); // Clear selected platforms
    setFromDate(""); // Clear fromDate
    setToDate(""); // Clear toDate

    // Clear localStorage for this column
    localStorage.removeItem(`searchInput-${columnIndex}`);
    localStorage.removeItem(`selectedPlatforms-${columnIndex}`);
    localStorage.removeItem(`fromDate-${columnIndex}`);
    localStorage.removeItem(`toDate-${columnIndex}`);

    // Collapse all subcolumns
    if (onExpand) {
      onExpand(columnIndex, []);
    }
  };

  const handleDeleteColumn = (index) => {
    if (expandedSubcolumns.length > 0) {
      toast.error(
        "Please close the sub-columns before deleting the main column."
      );
      return;
    }

    setColumns((prevColumns) => {
      if (prevColumns.length === 1) {
        // If there's only one column left, clear everything
        [
          "searchInput",
          "selectedPlatforms",
          "fromDate",
          "toDate",
          "subColumns",
        ].forEach((key) => localStorage.removeItem(`${key}-0`));
        setActiveDropdown(null);
        setExpandedSubcolumns([]);
        return [];
      }

      const updatedColumns = prevColumns.filter(
        (_, colIndex) => colIndex !== index
      );

      // 1️⃣ Shift local storage data
      for (let i = index; i < updatedColumns.length; i++) {
        [
          "searchInput",
          "selectedPlatforms",
          "fromDate",
          "toDate",
          "subColumns",
        ].forEach((key) => {
          const nextValue = localStorage.getItem(`${key}-${i + 1}`);
          if (nextValue !== null) {
            localStorage.setItem(`${key}-${i}`, nextValue);
          } else {
            localStorage.removeItem(`${key}-${i}`); // Ensure no stale data remains
          }
        });
      }

      // 2️⃣ Remove the last column’s data
      const lastIndex = updatedColumns.length;
      [
        "searchInput",
        "selectedPlatforms",
        "fromDate",
        "toDate",
        "subColumns",
      ].forEach((key) => localStorage.removeItem(`${key}-${lastIndex}`));

      // 3️⃣ Reset UI states
      setActiveDropdown(null);
      setExpandedSubcolumns([]);

      // Refresh the page after deletion
      setTimeout(() => {
        window.location.reload();
      }, 100); // Small delay to ensure state updates before reload

      return updatedColumns;
    });
  };

  // Function to get initial state from localStorage
  const getInitialState = () => {
    const storedSearchInput = localStorage.getItem(
      `searchInput-${columnIndex}`
    );
    const storedSelectedPlatforms = JSON.parse(
      localStorage.getItem(`selectedPlatforms-${columnIndex}`)
    );
    const storedFromDate = localStorage.getItem(`fromDate-${columnIndex}`);
    const storedToDate = localStorage.getItem(`toDate-${columnIndex}`);

    return {
      searchInput: storedSearchInput || "",
      selectedPlatforms: storedSelectedPlatforms || [],
      fromDate: storedFromDate || "", // Fetch stored fromDate
      toDate: storedToDate || "", // Fetch stored toDate
    };
  };

  // Initialize the state from localStorage or defaults
  useEffect(() => {
    const { searchInput, selectedPlatforms } = getInitialState();
    setSearchInput(searchInput);
    setSelectedPlatforms(selectedPlatforms);
  }, [columnIndex]);

  const filterRef = useRef(null);

useEffect(() => {
  const handleClickOutside = (event) => {
    if (filterRef.current && !filterRef.current.contains(event.target)) {
      setIsFilterVisible(false);
    }
  };

  document.addEventListener("mousedown", handleClickOutside);
  return () => {
    document.removeEventListener("mousedown", handleClickOutside);
  };
}, []);



  const handleSearchInputChange = (event) => {
    setSearchInput(event.target.value);
  };

  const handleSearchButtonClick = async (columnIndex) => {
    // Save the search state to local storage
    localStorage.setItem(`searchInput-${columnIndex}`, searchInput);
    localStorage.setItem(
      `selectedPlatforms-${columnIndex}`,
      JSON.stringify(selectedPlatforms)
    );
    localStorage.setItem(`fromDate-${columnIndex}`, fromDate);
    localStorage.setItem(`toDate-${columnIndex}`, toDate);

    // Hide any expanded subcolumns by setting them to an empty array
    setExpandedSubcolumns([]);

    // Trigger the search and fetch data
    await onSearch(
      columnIndex,
      searchInput,
      fromDate,
      toDate,
      selectedPlatforms
    );

    setSelectedPlatforms([]); // Uncomment this line if you want to also reset platform selection

    // Collapse any platform-specific UI elements or subcolumns
    if (onExpand) {
      onExpand(columnIndex, []); // Collapse all subcolumns by sending an empty array
    }
  };

  const handlePlatformToggle = (platform) => {
    // Update selected platforms but do not toggle subcolumns on platform icon click
    setSelectedPlatforms((prevSelected) => {
      const isPlatformSelected = prevSelected.includes(platform);
      const updatedPlatforms = isPlatformSelected
        ? prevSelected.filter((p) => p !== platform)
        : [...prevSelected, platform];

      // Only update selected platforms; no need to call onExpand for individual platform toggles
      return updatedPlatforms;
    });
  };

  const sortPosts = (type) => {
    const current = columns[columnIndex];
    if (!current || !Array.isArray(current.initialPosts)) return;
  
    // Toggle between descending and ascending order
    const isDescending = sortOrder === `${type}`; // If it's not descending, we want to set it to descending
  
    // Sort the posts based on likes or shares
    const sorted = [...current.initialPosts].sort((a, b) => {
      const aVal = type === "likes" ? getLikesValue(a) : getSharesValue(a);
      const bVal = type === "likes" ? getLikesValue(b) : getSharesValue(b);
  
      // Toggle between ascending and descending order
      return isDescending ? aVal - bVal : bVal - aVal;
    });
  
    const updatedColumns = [...columns];
    updatedColumns[columnIndex].posts = sorted;
    setColumns(updatedColumns);
  
    // Toggle the sort order: set it to "likes-desc" or "likes", "shares-desc" or "shares"
    setSortOrder(isDescending ? `${type}-desc` : type);
  
    // Sorting segregated (subcolumns)
    if (segregatedColumns?.[columnIndex]) {
      const sortedSubCols = segregatedColumns[columnIndex].map((subCol) => {
        const sortedPosts = [...(subCol.initialPosts || [])].sort((a, b) => {
          const aVal = type === "likes" ? getLikesValue(a) : getSharesValue(a);
          const bVal = type === "likes" ? getLikesValue(b) : getSharesValue(b);
          return isDescending ? aVal - bVal : bVal - aVal;
        });
        return {
          ...subCol,
          posts: sortedPosts,
        };
      });
  
      setSegregatedColumns((prev) => ({
        ...prev,
        [columnIndex]: sortedSubCols,
      }));
    }
  };
  
  
  
  
  
  
  
  // const filterPosts = (type) => {
  //   if (!columns || !Array.isArray(columns[columnIndex]?.posts)) {
  //     console.error("Invalid column or posts not found");
  //     return;
  //   }
  
  //   const getLikeCount = (post) =>
  //     post.reactions_total_count ?? post.likes_count ?? post.favorite_count ?? post.digg_count ?? 0;
  
  //   const getShareCount = (post) =>
  //     post.shares_count ?? post.share_count ?? 0;
  
  //   // 🔁 Sort main column posts
  //   const updatedColumns = columns.map((col, idx) => {
  //     if (idx !== columnIndex) return col;
  
  //     const sortedPosts = [...col.posts].sort((a, b) => {
  //       if (type === "likes") return getLikeCount(b) - getLikeCount(a);
  //       if (type === "shares") return getShareCount(b) - getShareCount(a);
  //       return 0;
  //     });
  
  //     return {
  //       ...col,
  //       posts: sortedPosts,
  //     };
  //   });
  //   setColumns(updatedColumns);
  
  //   // 🔁 Sort subcolumns
  //   if (segregatedColumns?.[columnIndex]) {
  //     const sortedSubCols = segregatedColumns[columnIndex].map((subCol) => {
  //       const sortedPosts = [...(subCol.initialPosts || [])].sort((a, b) => {
  //         const aVal = type === "likes" ? getLikesValue(a) : getSharesValue(a);
  //         const bVal = type === "likes" ? getLikesValue(b) : getSharesValue(b);
  //         return sortOrder === `${type}-desc` ? bVal - aVal : aVal - bVal;
  //       });
  
  //       return {
  //         ...subCol,
  //         posts: sortedPosts,
  //       };
  //     });
  
  //     setSegregatedColumns((prev) => ({
  //       ...prev,
  //       [columnIndex]: sortedSubCols,
  //     }));
  //   }
  
  //   // ✅ Set sort direction label (used for color toggle & labels)
  //   setSortOrder(sortOrder === `${type}-desc` ? `${type}` : `${type}-desc`);
  
  //   setActiveFilter(type); // Highlight the active filter button
  // };
  
  
  

  const handleAllPlatformsClick = () => {
    // Toggle the expansion of subcolumns for selected platforms
    if (expandedSubcolumns.length === 0) {
      // Expand subcolumns for the selected platforms
      setExpandedSubcolumns(selectedPlatforms);
      localStorage.setItem(
        `selectedPlatforms-${columnIndex}`,
        JSON.stringify(selectedPlatforms)
      ); // ✅ Update local storage
      if (onExpand) {
        onExpand(columnIndex, selectedPlatforms); // Expand only the selected platform's subcolumns
      }
    } else {
      // Collapse subcolumns for the selected platforms
      setExpandedSubcolumns([]);
      localStorage.setItem(
        `selectedPlatforms-${columnIndex}`,
        JSON.stringify([])
      ); // ✅ Clear selected platforms in local storage
      if (onExpand) {
        onExpand(columnIndex, []); // Collapse subcolumns
      }
    }
  };

  const toggleHelpModal = () => {
    setIsHelpOpen(!isHelpOpen); // Toggle the modal visibility
  };

  // Show the filter modal when the three-dot icon is clicked
  const handleFilterClick = () => {
    setIsFilterVisible(!isFilterVisible); // Toggle the visibility of the filter modal
  };
  
  
  
  
  // Normalize likes and shares fields
  const getLikesValue = (post) => {
    console.log('getLikesValue:', post);  // Debugging: Log the post data
    if (!post || !post.platform) return 0;
    switch (post.platform.toLowerCase()) {
      case "facebook": return post.reactions_total_count || 0;
      case "twitter": return post.favorite_count || 0;
      case "instagram": return post.likes_count || 0;
      case "tiktok": return post.digg_count || 0;
      default: return 0;
    }
  };
  
  const getSharesValue = (post) => {
    console.log('getSharesValue:', post);  // Debugging: Log the post data
    if (!post || !post.platform) return 0;
    switch (post.platform.toLowerCase()) {
      case "facebook": return post.shares_count || 0;
      case "tiktok": return post.share_count || 0;
      default: return 0;
    }
  };
  
  





const resetSorting = () => {
  const updatedColumns = [...columns];
  updatedColumns[columnIndex].posts = [...columns[columnIndex].initialPosts];
  setColumns(updatedColumns);
  setSortOrder(null);
};

  
  
  
  
  

  return (
    <div className={`search-area column-${columnIndex}`}>
      <button
        onClick={() => handleDeleteColumn(columnIndex)}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "#e74c3c",
          fontSize: "14px",
          fontWeight: "bold",
          display: "flex",
          alignItems: "center",
          gap: "5px",
          width: "25%",
          padding: "5px",
          textAlign: "left",
          transition: "background 0.2s ease-in-out",
          marginLeft: "75%",
        }}
        onMouseEnter={(e) =>
          (e.target.style.background = "rgba(231, 76, 60, 0.1)")
        }
        onMouseLeave={(e) => (e.target.style.background = "none")}
      >
        <FontAwesomeIcon icon={faTrash} />
        Delete
      </button>
      <div
        className={`search-area column-${columnIndex}`}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",

          borderRadius: "8px",

          backgroundColor: "#fff",
        }}
      >
        {/* Top Row - Search Input and Button */}
        <div
          className={`search-row column-${columnIndex}`}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            maxWidth: "500px", // Keeps the input width balanced
            gap: "10px",
          }}
        >
          {/* Container for search input with embedded help icon */}
          <div
            style={{
              position: "relative", // Sets the positioning context
              display: "flex",
              width: "100%", // Ensures the container takes full width
            }}
          >
            {/* Search Input */}
            <input
              type="text"
              className={`search-input column-${columnIndex}`}
              value={searchInput}
              placeholder="Keyword (comma, AND/OR, dorking syntax)"
              onChange={handleSearchInputChange}
              disabled={isLoading}
              style={{
                width: "100%", // Ensures the input takes the full width of its container
                padding: "12px",
                paddingRight: "40px", // Makes space for the help icon inside the input
                borderRadius: "5px",
                border: "1px solid #ccc",
                fontSize: "16px",
                boxShadow: "inset 0 2px 4px rgba(0,0,0,0.1)",
              }}
            />

            {/* Help Button positioned within the input field */}
            <div
              style={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "24px",
                height: "24px",
                borderRadius: "50%",
                fontSize: "18px",
                transition: "0.3s",
                backgroundColor: "transparent",
                border: "2px solid #3498db",
                color: "#3498db",
                position: "absolute",
                right: "8px", // Position it on the right within the input field
                top: "45%",
                transform: "translateY(-50%)", // Centers it vertically
              }}
              onClick={toggleHelpModal}
            >
              ?
            </div>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "15px",
            width: "100%",
            position: "relative",
          }}
        >
          <button
            onClick={() => {
              handleSearchButtonClick(columnIndex); // Call the first function
              apihandleSearch(searchInput, columnIndex, selectedPlatforms); // Call the second function
            }}
            disabled={isLoading}
            style={{
              padding: "10px 12px",
              borderRadius: "5px",
              border: "1px solid #000", // Neutral border to complement the vibrant colors
              background: isLoading ? "#ccc" : "#0288C0", // Gradient effect when not loading
              color: "#fff",
              fontSize: "16px",
              cursor: isLoading ? "default" : "pointer",
              transition: "0.3s ease",
              boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
              marginBottom: "10px",
              width: "50%",
              opacity: isLoading ? 0.7 : 1, // Reduce opacity when disabled
            }}
          >
            <FontAwesomeIcon icon={faSearch} size="lg" />
          </button>

          {/* Reset Button */}
          <div
            onClick={() => handleReset(columnIndex)}
            style={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              fontSize: "18px",
              transition: "0.3s",
              backgroundColor: "transparent",
              border: "2px solid #e74c3c",
              color: "#e74c3c",
              position: "relative",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.children[0].style.opacity = "1")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.children[0].style.opacity = "0")
            }
          >
            {/* Message that appears on hover */}
            <span
              style={{
                position: "absolute",
                bottom: "130%", // Position above the button
                backgroundColor: "#333",
                color: "#fff",
                fontSize: "12px",
                padding: "5px 8px",
                borderRadius: "5px",
                whiteSpace: "nowrap",
                opacity: "0",
                transition: "opacity 0.3s ease",
                pointerEvents: "none", // Prevents interaction issues
              }}
            >
              Click to Reset Search
            </span>

            <FontAwesomeIcon icon={faSyncAlt} size="lg" />
          </div>
        </div>

        <div
          style={{
            width: "100%",
            maxWidth: "500px",
            height: "1px",
            backgroundColor: "#ddd",
            marginTop: "10px",
            marginBottom: "10px",
          }}
        ></div>

        {/* Help Modal */}
        {isHelpOpen && (
          <div
            className="help-modal"
            style={{
              position: "absolute",
              top: "50px",
              right: "0",
              backgroundColor: "#fff",
              border: "1px solid #ddd",
              padding: "12px",
              borderRadius: "8px",
              boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
              width: "270px",
              zIndex: 100,
              textAlign: "left",
              fontSize: "13px",
            }}
          >
            {/* Header */}
            <div
              style={{
                fontWeight: "bold",
                fontSize: "14px",
                color: "#007BFF",
                marginBottom: "5px",
                borderBottom: "1px solid #ddd",
                paddingBottom: "5px",
              }}
            >
              🔍 Search Tips
            </div>

            {/* Help Content */}
            <ul
              style={{
                paddingLeft: "18px",
                margin: "0",
                listStyleType: "disc",
                lineHeight: "1.5",
              }}
            >
              <li>
                Use <b>keywords</b> separated by <b>commas</b>.
              </li>
              <li>
                Supports <b>AND / OR</b> operators for better filtering.
              </li>
              <li>
                Use <b>dorking syntax</b> for advanced searches.
              </li>
              <li>
                Example: <code>keyword1, keyword2 AND keyword3</code>
              </li>
            </ul>

            {/* Close Button */}
            <div style={{ textAlign: "right", marginTop: "8px" }}>
              <button
                onClick={toggleHelpModal}
                style={{
                  backgroundColor: "#007BFF",
                  color: "white",
                  fontSize: "12px",
                  padding: "5px 10px",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Platform Selection with Icons */}
      <div
        className={`deck-platform-row column-${columnIndex}`}
        style={{
          display: "flex",
          justifyContent: "center", // Centers icons horizontally
          alignItems: "center", // Centers icons vertically
          width: "100%", // Ensures it takes full width if needed
          marginBottom: "10px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          {/* Three-Dot Icon for Filtering */}
          <div
            onClick={() => handleFilterClick()}
            style={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "35px",
              height: "35px",
              borderRadius: "50%",
              padding: "5px",
              border: "2px solid #ccc", // Styling for the icon
              transition: "0.3s",
            }}
          >
            <FontAwesomeIcon icon={faSortAmountDown} style={{ color: "#65ccb7" }} />



          </div>

          {/* Facebook Icon */}
          <img
            key="facebook"
            src="/icons/facebook.png"
            alt="Facebook"
            onClick={() => handlePlatformToggle("facebook")}
            style={{
              width: "35px",
              height: "35px",
              cursor: "pointer",
              borderRadius: "50%",
              padding: "5px",
              border: selectedPlatforms.includes("facebook")
                ? "2px solid #3498db"
                : "2px solid transparent",
              transition: "0.3s",
            }}
          />
          {/* Instagram Icon */}
          <img
            key="instagram"
            src="/icons/instagram.png"
            alt="Instagram"
            onClick={() => handlePlatformToggle("instagram")}
            style={{
              width: "35px",
              height: "35px",
              cursor: "pointer",
              borderRadius: "50%",
              padding: "5px",
              border: selectedPlatforms.includes("instagram")
                ? "2px solid #3498db"
                : "2px solid transparent",
              transition: "0.3s",
            }}
          />
          {/* Twitter Icon */}
          <img
            key="twitter"
            src="/icons/twitter.png"
            alt="Twitter"
            onClick={() => handlePlatformToggle("twitter")}
            style={{
              width: "35px",
              height: "35px",
              cursor: "pointer",
              borderRadius: "50%",
              padding: "5px",
              border: selectedPlatforms.includes("twitter")
                ? "2px solid #3498db"
                : "2px solid transparent",
              transition: "0.3s",
            }}
          />
          {/* TikTok Icon */}
          <img
            key="tiktok"
            src="/icons/tiktok.png"
            alt="TikTok"
            onClick={() => handlePlatformToggle("tiktok")}
            style={{
              width: "35px",
              height: "35px",
              cursor: "pointer",
              borderRadius: "50%",
              padding: "5px",
              border: selectedPlatforms.includes("tiktok")
                ? "2px solid #3498db"
                : "2px solid transparent",
              transition: "0.3s",
            }}
          />
        </div>

        {/* Icon for expanding subcolumns of selected platforms */}
        <div
          onClick={handleAllPlatformsClick}
          style={{
            cursor: "pointer",
          }}
        >
          <img
            src="/icons/all.png"
            alt="All Platforms"
            title="Click to expand subcolumns" // This adds the hover tooltip
            style={{ width: "35px", height: "35px" }}
          />
        </div>

        {/* Modal for filtering posts by likes and shares */}
        {isFilterVisible && (
  <div
    ref={filterRef}
    style={{
      position: "absolute",

    top: "110px",
    right: "0",
    backgroundColor: "white",
    border: "1px solid #ccc",
    padding: "10px",
    borderRadius: "5px",
    boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)",
    zIndex: 999,
    width: "200px",
  }}>
      <div style={{ textAlign: "right", marginBottom: "5px" }}>
    <button
      onClick={() => setIsFilterVisible(false)}
      style={{
        background: "none",
        border: "none",
        fontSize: "16px",
        cursor: "pointer",
        color: "#888",
      }}
    >
      ❌
    </button>
  </div>

  <button
  onClick={() => sortPosts("likes")}
  style={{
    padding: "5px 10px",
    width: "100%",
    marginBottom: "10px",
    backgroundColor: "#65ccb7",
    color: "#fff",
    borderRadius: "4px",
    border: "none",
  }}
>
  <FontAwesomeIcon
    icon={faThumbsUp}
    style={{
      marginRight: "8px",
      color: sortOrder?.includes("likes") ? "#f1c40f" : "#fff",  // Yellow if active
    }}
  />
  {sortOrder === "likes-desc"
    ? "More Likes First"  // For descending, show "More Likes First
    : "Less Likes First"}  
</button>

<button
  onClick={() => sortPosts("shares")}
  style={{
    padding: "5px 10px",
    width: "100%",
    backgroundColor: "#65ccb7",
    color: "#fff",
    borderRadius: "4px",
    border: "none",
  }}
>
  <FontAwesomeIcon
    icon={faRetweet}
    style={{
      marginRight: "8px",
      color: sortOrder?.includes("shares") ? "#fa0202" : "#fff",  // Red if active
    }}
  />
  {sortOrder === "shares-desc"
    ? "More Shares First"  // For descending, show "More Shares First"
    : "Less Shares First"}  
</button>



    {sortOrder && (
      <button
        onClick={resetSorting}
        style={{
          padding: "5px 10px",
          width: "100%",
          backgroundColor: "#e74c3c",
          color: "#fff",
          borderRadius: "4px",
          border: "none",
          marginTop: "10px",
        }}
      >
        Reset Sorting
      </button>
    )}
  </div>
)}



      </div>
    </div>
  );
};

export default SearchArea;

